import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { AnnouncementBanner } from "./announcement-banner";

const HIDE_THRESHOLD = 950; // px — banner stays visible below this scroll position
const JITTER_THRESHOLD = 10; // px — ignore tiny scroll deltas

export function AnnouncementBannerSlot({ mode }) {
  const [visible, setVisible] = useState(true);
  const lastYRef = useRef(0);
  const bannerHeightRef = useRef(44);
  const roRef = useRef(null);

  // Measure stable content height so ResizeObserver doesn't fire repeatedly during collapse animations
  const contentRef = useCallback((node) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }

    if (node) {
      if (typeof ResizeObserver === "undefined") {
        bannerHeightRef.current = node.offsetHeight || 44;
        document.documentElement.style.setProperty(
          "--banner-h",
          visible ? `${bannerHeightRef.current}px` : "0px"
        );
        return;
      }
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const h = entry.target.offsetHeight || Math.round(entry.contentRect.height);
          if (h > 0) {
            bannerHeightRef.current = h;
            if (visible) {
              document.documentElement.style.setProperty("--banner-h", `${h}px`);
            }
          }
        }
      });
      ro.observe(node);
      roRef.current = ro;
    }
  }, [visible]);

  // Update --banner-h once cleanly on visibility change (eliminates continuous layout thrashing)
  useEffect(() => {
    if (mode === "hidden") {
      document.documentElement.style.setProperty("--banner-h", "0px");
      return;
    }
    if (visible) {
      document.documentElement.style.setProperty(
        "--banner-h",
        `${bannerHeightRef.current || 44}px`
      );
    } else {
      document.documentElement.style.setProperty("--banner-h", "0px");
    }
  }, [visible, mode]);

  // Show/hide on scroll direction; always visible below HIDE_THRESHOLD
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < HIDE_THRESHOLD) {
        setVisible(true);
        lastYRef.current = y;
        return;
      }
      const delta = y - lastYRef.current;
      if (Math.abs(delta) < JITTER_THRESHOLD) return;
      lastYRef.current = y;
      setVisible(delta < 0); // scroll-up → show, scroll-down → hide
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (mode === "hidden") return null;

  return (
    <motion.div
      animate={{ height: visible ? "auto" : 0, opacity: visible ? 1 : 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="fixed top-0 left-0 right-0 z-[60] overflow-hidden"
    >
      <div ref={contentRef}>
        <AnnouncementBanner />
      </div>
    </motion.div>
  );
}
